'use client';

import { Router } from 'next/router';
import { useEffect, useState } from 'react';

export type IPageRouteOptions = {
  shallow: boolean;
};

export type IPageRoute = {
  pathname: string;
  query: Record<string, string>;
  asPath: string;
};

function getSearchParams(route: string) {
  const components = route.split('?');
  const search = components[1] || '';
  const searchParams = new URLSearchParams(search);
  return Object.fromEntries(searchParams);
}

function getPageRoute(route: string): IPageRoute {
  const pageRoute = {
    pathname: route.split('?')[0]!,
    query: getSearchParams(route),
    asPath: route,
  };
  return pageRoute;
}

export function usePageLoading(options: { loading: boolean } = { loading: false }) {
  const [loading, setLoading] = useState<boolean>(options.loading);
  const [error, setError] = useState<void | Error>();
  const [pageRoute, setPageRoute] = useState<IPageRoute>();
  const [previousPageRoute, setPreviousPageRoute] = useState<IPageRoute>();

  useEffect(() => {
    let previousRoute: string | undefined;
    const onRouteChangeStart = (route: string, options: IPageRouteOptions) => {
      const pageRoute = previousRoute ? getPageRoute(previousRoute) : undefined;
      // console.log('usePageLoading.onRouteChangeStart', pageRoute, previousRoute);
      setError();
      setPreviousPageRoute(pageRoute);
      // shallow routes do not fire loading state
      if (options?.shallow) {
        return;
      }
      setLoading(true);
    };
    const onRouteChangeComplete = (route: string, options: IPageRouteOptions) => {
      const pageRoute = getPageRoute(route);
      // console.log('usePageLoading.onRouteChangeComplete', pageRoute);
      setPageRoute(pageRoute);
      previousRoute = route;
      // shallow routes do not fire loading state
      if (options?.shallow) {
        return;
      }
      setLoading(false);
    };
    const onRouteChangeError = (error: Error, route: string, options: IPageRouteOptions) => {
      console.log('usePageLoading.onRouteChangeError', error);
      setError(error);
      // shallow routes do not fire loading state
      if (options?.shallow) {
        return;
      }
      setLoading(false);
    };
    Router.events.on('routeChangeStart', onRouteChangeStart);
    Router.events.on('routeChangeComplete', onRouteChangeComplete);
    Router.events.on('routeChangeError', onRouteChangeError);
    return () => {
      Router.events.off('routeChangeStart', onRouteChangeStart);
      Router.events.off('routeChangeComplete', onRouteChangeComplete);
      Router.events.off('routeChangeError', onRouteChangeError);
    };
  }, []);

  return { loading, error, pageRoute, previousPageRoute };
}
